import { CommonModule } from '@angular/common';
import { Injector, NgModule } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DynamicLayoutModule } from '@common-modules/dynamic-layout/dynamic-layout.module';
import { EnhancedInputSelectComponent } from '@common-modules/shared-component/enhanced-input-select/enhanced-input-select.component';
import { CommonSharedModule } from '@common-modules/shared/common-shared.module';
import { IconModule } from '@common-modules/shared/icon/icon.module';
import { WidgetRegistryService } from '@common-modules/widget-registry/widget-registry';
import { GenericChartsModuleSettings } from '@common-modules/wlm-charts/generic-charts-module-settings';
import { GenericChartsModule } from '@common-modules/wlm-charts/generic-charts.module';
import { WLMGridModule } from '@common-modules/wlm-grid/wlm-grid.module';
import { WlmMapModule } from '@common-modules/wlm-map/wlm-map.module';
import { TranslateModule } from '@ngx-translate/core';
import { InstanceUpdatesManagerService } from '../../../../cloud-twin/src/app/cloud-twin-shared/workers/instance-updates-manager.service';
import { InstanceUpdatesWorkerLoaderService } from '../../../../cloud-twin/src/app/cloud-twin-shared/workers/instance-updates-worker-loader.service';
import { AttributesChartPopup } from './attributes-chart-popup/attributes-chart-popup.component';
import { AttributesChartService } from './attributes-chart-popup/attributes-chart.service';
import { MasterOperatorAttributeWidgetComponent } from './master-operator-attribute-widget/master-operator-attribute-widget.component';
import { MasterOperatorAttributesWidgetComponent } from './master-operator-attributes-widget/master-operator-attributes-widget.component';
import { MasterOperatorConnectFormComponent } from './master-operator-connect-form/master-operator-connect-form.component';
import { MasterOperatorConnectIndicatorComponent } from './master-operator-connect-indicator/master-operator-connect-indicator.component';
import { MasterOperatorConnectPopup } from './master-operator-connect-popup/master-operator-connect-popup.component';
import { MasterOperatorConnectComponent } from './master-operator-connect/master-operator-connect.component';
import { MasterOperatorGroupToolbarComponent } from './master-operator-group-toolbar/master-operator-group-toolbar.component';
import { MasterOperatorGroupWidgetComponent } from './master-operator-group-widget/master-operator-group-widget.component';
import { MasterOperatorGroupComponent } from './master-operator-group/master-operator-group.component';
import { MasterOperatorMapButtonsComponent } from './master-operator-map-buttons/master-operator-map-buttons.component';
import { MasterOperatorMapWidgetComponent } from './master-operator-map-widget/master-operator-map-widget.component';
import { MasterOperatorRealtimeConnectPopup } from './master-operator-realtime-connect-popup/master-operator-realtime-connect-popup.component';
import { MasterOperatorSimulateFormSidepanelComponent } from './master-operator-simulate-form-sidepanel/master-operator-simulate-form-sidepanel.component';
import { MasterOperatorSimulateFormComponent } from './master-operator-simulate-form/master-operator-simulate-form.component';
import { TCSimulationService } from './master-operator-simulate-form/ts-simulation.service';
import { MasterOperatorSingleToolbarComponent } from './master-operator-single-toolbar/master-operator-single-toolbar.component';
import { MasterOperatorSingleWidgetComponent } from './master-operator-single-widget/master-operator-single-widget.component';
import { MasterOperatorSingleComponent } from './master-operator-single/master-operator-single.component';
import { MasterOperatorWorkspaceFormComponent } from './master-operator-workspace-form/master-operator-workspace-form.component';
import { MasterOperatorWorkspacePopupComponent } from './master-operator-workspace-popup/master-operator-workspace-popup.component';
import { MoConnectFormInstanceOptionComponent } from './mo-connect-form-instance-option/mo-connect-form-instance-option.component';
import { MoConnectFormScenarioOptionComponent } from './mo-connect-form-scenario-option/mo-connect-form-scenario-option.component';
import { MoConnectFormSimulationOptionComponent } from './mo-connect-form-simulation-option/mo-connect-form-simulation-option.component';
import { ProfileChartPopupComponent } from './profiles/profile-chart-popup/profile-chart-popup.component';
import { ProfileChartService } from './profiles/profile-chart-popup/profile-chart.service';
import { ProfilesListComponent } from './profiles/profiles-list/profiles-list.component';
import { ReportListComponent } from './reports/report-list/report-list.component';
import { MasterOperatorService } from './services/master-operator.service';
import { ProfilesService } from './services/profiles.service';
import { ReportsService } from './services/reports.service';
import { ScenarioInfoService } from './services/scenario-info.service';

@NgModule({
  declarations: [
    MasterOperatorSingleComponent,
    MasterOperatorSingleWidgetComponent,
    MasterOperatorConnectIndicatorComponent,
    MasterOperatorMapWidgetComponent,
    MasterOperatorAttributesWidgetComponent,
    MasterOperatorConnectComponent,
    MasterOperatorGroupComponent,
    MasterOperatorGroupWidgetComponent,
    MasterOperatorGroupToolbarComponent,
    MasterOperatorAttributeWidgetComponent,
    MasterOperatorWorkspacePopupComponent,
    MasterOperatorWorkspaceFormComponent,
    MasterOperatorConnectFormComponent,
    AttributesChartPopup,
    MasterOperatorSingleToolbarComponent,
    MasterOperatorSimulateFormComponent,
    MasterOperatorSimulateFormSidepanelComponent,
    MasterOperatorConnectPopup,
    MasterOperatorRealtimeConnectPopup,
    MoConnectFormInstanceOptionComponent,
    MoConnectFormSimulationOptionComponent,
    MoConnectFormScenarioOptionComponent,
    ProfilesListComponent,
    MasterOperatorMapButtonsComponent,
    ProfileChartPopupComponent,
    ReportListComponent,
  ],
  exports: [
    MasterOperatorSingleWidgetComponent,
    MasterOperatorConnectIndicatorComponent,
    MasterOperatorGroupToolbarComponent,
    MasterOperatorSingleToolbarComponent,
  ],
  imports: [
    CommonModule,
    IconModule,
    CommonSharedModule,
    DynamicLayoutModule,
    TranslateModule,
    WlmMapModule,
    WLMGridModule,
    GenericChartsModule.forFeature(new GenericChartsModuleSettings({ avoidConversion: true })),
    MatFormFieldModule,
    MatInputModule,
    EnhancedInputSelectComponent,
  ],
  providers: [
    ScenarioInfoService,
    ProfilesService,
    ReportsService,
    MasterOperatorService,
    { provide: 'MasterOperatorService', useExisting: MasterOperatorService },
    AttributesChartService,
    { provide: 'AttributesChartService', useExisting: AttributesChartService },
    ProfileChartService,
    { provide: 'ProfileChartService', useExisting: ProfileChartService },
    TCSimulationService,
    InstanceUpdatesWorkerLoaderService,
    InstanceUpdatesManagerService,
  ],
})
export class MasterOperatorModule {
  static injector: Injector;
  constructor(
    injector: Injector,
    private _widgetRegistry: WidgetRegistryService,
  ) {
    MasterOperatorModule.injector = injector;
    this._widgetRegistry.register([
      {
        name: 'MasterOperatorSingleWidgetComponent',
        widgetClass: MasterOperatorSingleWidgetComponent,
      },
      {
        name: 'MasterOperatorGroupWidgetComponent',
        widgetClass: MasterOperatorGroupWidgetComponent,
      },
      {
        name: 'MasterOperatorMapWidgetComponent',
        widgetClass: MasterOperatorMapWidgetComponent,
      },
      {
        name: 'MasterOperatorAttributesWidgetComponent',
        widgetClass: MasterOperatorAttributesWidgetComponent,
      },
      {
        name: 'MasterOperatorAttributeWidgetComponent',
        widgetClass: MasterOperatorAttributeWidgetComponent,
      },
    ]);
  }
}
